import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';

type EntityRefDefaults = {
  defaultKind: string;
  defaultNamespace?: string;
};

export type TeamApiRelation = 'owned' | 'provided' | 'consumed';

export interface TeamApiSummary {
  ref: string;
  entity: Entity;
  relations: TeamApiRelation[];
}

export interface TeamSummary {
  ref: string;
  name: string;
  namespace: string;
  displayName: string;
  groupEntity?: Entity;
  services: Entity[];
  apis: TeamApiSummary[];
}

const DEFAULT_NAMESPACE = 'default';

export const getEntityNamespace = (entity: Entity) =>
  entity.metadata.namespace || DEFAULT_NAMESPACE;

export const getEntityRoute = (entity: Entity) =>
  `/catalog/${getEntityNamespace(entity)}/${entity.kind.toLowerCase()}/${
    entity.metadata.name
  }`;

export const getApiDefinitionRoute = (entity: Entity) =>
  `${getEntityRoute(entity)}/definition`;

export const normalizeEntityRef = (
  value: string | undefined,
  defaults: EntityRefDefaults,
) => {
  if (!value || typeof value !== 'string') {
    return undefined;
  }

  try {
    return stringifyEntityRef(
      parseEntityRef(value, {
        defaultKind: defaults.defaultKind,
        defaultNamespace: defaults.defaultNamespace || DEFAULT_NAMESPACE,
      }),
    ).toLocaleLowerCase('en-US');
  } catch {
    return undefined;
  }
};

export const normalizeOwnerRef = (value: string | undefined) =>
  normalizeEntityRef(value, {
    defaultKind: 'Group',
    defaultNamespace: DEFAULT_NAMESPACE,
  });

export const normalizeApiRef = (
  value: string | undefined,
  defaultNamespace = DEFAULT_NAMESPACE,
) =>
  normalizeEntityRef(value, {
    defaultKind: 'API',
    defaultNamespace,
  });

export const getEntityRef = (entity: Entity) =>
  stringifyEntityRef({
    kind: entity.kind,
    namespace: getEntityNamespace(entity),
    name: entity.metadata.name,
  }).toLocaleLowerCase('en-US');

export const isApiEntity = (entity: Entity) =>
  entity.kind.toLowerCase() === 'api';

export const isServiceEntity = (entity: Entity) =>
  entity.kind.toLowerCase() === 'component' &&
  typeof (entity as any).spec?.type === 'string' &&
  (entity as any).spec.type.toLowerCase() === 'service';

export const getApiType = (entity: Entity) => {
  const type = (entity as any).spec?.type;
  return typeof type === 'string' ? type.toLowerCase() : undefined;
};

const getEntityDisplayName = (entity: Entity) =>
  entity.metadata.title || entity.metadata.name;

const getRefName = (entityRef: string) => {
  const parsed = parseEntityRef(entityRef);
  return parsed.name;
};

const getRefNamespace = (entityRef: string) => {
  const parsed = parseEntityRef(entityRef);
  return parsed.namespace || DEFAULT_NAMESPACE;
};

const getApiReferences = (
  entity: Entity,
  field: 'providesApis' | 'consumesApis',
) => {
  const refs = (entity as any).spec?.[field];
  return Array.isArray(refs) ? refs.filter(ref => typeof ref === 'string') : [];
};

const addTeamApi = (
  apis: Map<string, TeamApiSummary>,
  api: Entity | undefined,
  relation: TeamApiRelation,
) => {
  if (!api) {
    return;
  }

  const ref = getEntityRef(api);
  const current = apis.get(ref);
  if (!current) {
    apis.set(ref, {
      ref,
      entity: api,
      relations: [relation],
    });
    return;
  }

  if (!current.relations.includes(relation)) {
    current.relations.push(relation);
  }
};

export const getEntityApiSummaries = (
  entity: Entity,
  allEntities: Entity[],
): TeamApiSummary[] => {
  const apis = new Map<string, TeamApiSummary>();
  const apiEntities = new Map(
    allEntities.filter(isApiEntity).map(api => [getEntityRef(api), api]),
  );

  if (isApiEntity(entity)) {
    addTeamApi(apis, entity, 'owned');
  }

  const entityNamespace = getEntityNamespace(entity);
  getApiReferences(entity, 'providesApis').forEach(apiRef => {
    const normalizedRef = normalizeApiRef(apiRef, entityNamespace);
    addTeamApi(
      apis,
      normalizedRef ? apiEntities.get(normalizedRef) : undefined,
      'provided',
    );
  });

  getApiReferences(entity, 'consumesApis').forEach(apiRef => {
    const normalizedRef = normalizeApiRef(apiRef, entityNamespace);
    addTeamApi(
      apis,
      normalizedRef ? apiEntities.get(normalizedRef) : undefined,
      'consumed',
    );
  });

  return Array.from(apis.values()).sort((a, b) =>
    a.entity.metadata.name.localeCompare(b.entity.metadata.name),
  );
};

export const buildTeamSummaries = (entities: Entity[]): TeamSummary[] => {
  const teams = new Map<
    string,
    Omit<TeamSummary, 'apis'> & {
      apiMap: Map<string, TeamApiSummary>;
    }
  >();
  const apiEntities = new Map<string, Entity>();

  const ensureTeam = (ref: string, groupEntity?: Entity) => {
    const existing = teams.get(ref);
    if (existing) {
      if (groupEntity && !existing.groupEntity) {
        existing.groupEntity = groupEntity;
        existing.displayName = getEntityDisplayName(groupEntity);
      }
      return existing;
    }

    const team = {
      ref,
      name: getRefName(ref),
      namespace: getRefNamespace(ref),
      displayName: groupEntity
        ? getEntityDisplayName(groupEntity)
        : getRefName(ref),
      groupEntity,
      services: [],
      apiMap: new Map<string, TeamApiSummary>(),
    };
    teams.set(ref, team);
    return team;
  };

  entities.forEach(entity => {
    if (isApiEntity(entity)) {
      apiEntities.set(getEntityRef(entity), entity);
    }

    if (entity.kind.toLowerCase() === 'group') {
      ensureTeam(getEntityRef(entity), entity);
    }
  });

  entities.forEach(entity => {
    const ownerRef = normalizeOwnerRef((entity as any).spec?.owner);
    if (!ownerRef) {
      return;
    }

    const team = ensureTeam(ownerRef);

    if (isServiceEntity(entity)) {
      team.services.push(entity);
    }

    if (isApiEntity(entity)) {
      addTeamApi(team.apiMap, entity, 'owned');
    }
  });

  teams.forEach(team => {
    team.services.forEach(service => {
      const serviceNamespace = getEntityNamespace(service);
      getApiReferences(service, 'providesApis').forEach(apiRef => {
        const normalizedRef = normalizeApiRef(apiRef, serviceNamespace);
        addTeamApi(
          team.apiMap,
          normalizedRef ? apiEntities.get(normalizedRef) : undefined,
          'provided',
        );
      });

      getApiReferences(service, 'consumesApis').forEach(apiRef => {
        const normalizedRef = normalizeApiRef(apiRef, serviceNamespace);
        addTeamApi(
          team.apiMap,
          normalizedRef ? apiEntities.get(normalizedRef) : undefined,
          'consumed',
        );
      });
    });
  });

  return Array.from(teams.values())
    .map(team => ({
      ref: team.ref,
      name: team.name,
      namespace: team.namespace,
      displayName: team.displayName,
      groupEntity: team.groupEntity,
      services: team.services.sort((a, b) =>
        a.metadata.name.localeCompare(b.metadata.name),
      ),
      apis: Array.from(team.apiMap.values()).sort((a, b) =>
        a.entity.metadata.name.localeCompare(b.entity.metadata.name),
      ),
    }))
    .filter(
      team =>
        team.groupEntity || team.services.length > 0 || team.apis.length > 0,
    )
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
};

export const filterTeamApisByType = (
  apis: TeamApiSummary[],
  typeFilter: 'all' | 'openapi' | 'asyncapi',
) => {
  if (typeFilter === 'all') {
    return apis;
  }

  return apis.filter(api => getApiType(api.entity) === typeFilter);
};

export const entityMatchesTypeFilter = (
  entity: Entity,
  typeFilter: string,
  allEntities: Entity[],
) => {
  // Get the entity's spec.type
  const entityType = (entity as any).spec?.type;
  const normalizedEntityType =
    entityType && typeof entityType === 'string'
      ? entityType.toLowerCase()
      : undefined;

  // For non-API filters (like 'library', 'service', 'website', etc.)
  if (typeFilter !== 'openapi' && typeFilter !== 'asyncapi') {
    return normalizedEntityType === typeFilter;
  }

  // For API definition filters (openapi/asyncapi)
  if (isApiEntity(entity)) {
    return normalizedEntityType === typeFilter;
  }

  // For components, check if they provide/consume APIs of the specified type
  if (entity.kind.toLowerCase() !== 'component') {
    return false;
  }

  const apiEntities = new Map(
    allEntities.filter(isApiEntity).map(api => [getEntityRef(api), api]),
  );
  const componentNamespace = getEntityNamespace(entity);
  const referencedApis = [
    ...getApiReferences(entity, 'providesApis'),
    ...getApiReferences(entity, 'consumesApis'),
  ];

  return referencedApis.some(apiRef => {
    const normalizedRef = normalizeApiRef(apiRef, componentNamespace);
    const api = normalizedRef ? apiEntities.get(normalizedRef) : undefined;
    return api ? getApiType(api) === typeFilter : false;
  });
};
